// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { AppIcon } from '@loa/ui-components';
import './completion.css';

/**
 * Icons via AppIcon
 */
const Icons = {
  arrowRight: <AppIcon name="angle right" size={20} />,
  send: <AppIcon name="send" size={20} />,
  warning: <AppIcon name="severity warning" size={20} />,
  loader: (
    <svg className="wizard-btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" strokeOpacity="1" />
    </svg>
  ),
};

export interface CompletionActionsProps {
  /**
   * Callback when user chooses to skip and view relationship
   */
  onSkip: () => void;
  /**
   * Callback when user chooses to send invitations
   */
  onSendInvitations: () => void;
  /**
   * Whether invitations are being sent
   */
  isSending: boolean;
  /**
   * Error message from sending attempt
   */
  sendingError: string | null;
  /**
   * Number of clients to send invitations to
   */
  clientCount: number;
}

/**
 * CompletionActions - Action buttons for the completion screen
 *
 * Provides:
 * - Skip button (view relationship without sending)
 * - Send invitations button with loading state
 * - Error message display
 */
export const CompletionActions = ({
  onSkip,
  onSendInvitations,
  isSending,
  sendingError,
  clientCount,
}: CompletionActionsProps) => {
  const clientText = clientCount === 1 ? '1 Client' : `${clientCount} Clients`;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      {/* Error message */}
      {sendingError && (
        <div className="wizard-error-message" style={{ marginBottom: '1rem' }}>
          <div className="wizard-error-message__title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ width: '16px', height: '16px' }}>{Icons.warning}</span>
              {' '}Failed to Send Invitations
            </span>
          </div>
          <p style={{ margin: '0.5rem 0 0', color: '#721c24' }}>{sendingError}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="wizard-navigation">
        <div className="wizard-navigation__left">
          <button
            type="button"
            className="wizard-prev-btn"
            onClick={onSkip}
            disabled={isSending}
          >
            {Icons.arrowRight}
            Skip & View Relationship
          </button>
        </div>

        <div className="wizard-navigation__right">
          <button
            type="button"
            className="wizard-primary-btn"
            onClick={onSendInvitations}
            disabled={isSending || clientCount === 0}
          >
            {isSending ? Icons.loader : Icons.send}
            {isSending ? 'Sending...' : `Send Invitations to ${clientText}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletionActions;
